import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaEye,
  FaMoneyBillWave,
  FaTimesCircle,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import {
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiDollarSign,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiUploadCloud,
  FiCheck
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const statusColors = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-gray-50 text-gray-700 border-gray-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
};

const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [loadingPay, setLoadingPay] = useState(false);
  const [loading, setLoading] = useState(true);

  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10,
  });

  const fetchInvoices = async (page = 1, status = "all") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const params = { page, per_page: meta.per_page, search };
      if (status !== "all") params.status = status;

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/invoice`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      setInvoices(res.data.data || []);
      setMeta(res.data.meta || { current_page: page, last_page: 1, total: (res.data.data || []).length, per_page: 10 });
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(currentPage, filteredStatus);
  }, [currentPage, filteredStatus, search]);

  const handleCreate = () => navigate("/inventory/invoice/create");

  const handlePay = async () => {
    if (!evidenceFile) {
      Swal.fire({
        icon: "warning",
        title: "Bukti Pembayaran Belum Diupload",
        text: "Silakan pilih atau unggah file bukti transfer/pembayaran invoice!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setLoadingPay(true);
    const formData = new FormData();
    formData.append("files", evidenceFile);

    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/invoice/pay/${selectedInvoice.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );

      await Swal.fire({
        title: "Pembayaran Berhasil!",
        text: `Invoice ${selectedInvoice.number} telah ditandai lunas.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      setIsModalOpen(false);
      setEvidenceFile(null);
      fetchInvoices(currentPage, filteredStatus);
    } catch (error) {
      console.error("Error making payment:", error);
      Swal.fire({
        title: "Gagal Pembayaran",
        text: error.response?.data?.message || "Terjadi kesalahan server saat memproses pembayaran.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingPay(false);
    }
  };

  const handleCancel = async (id) => {
    const confirm = await Swal.fire({
      title: "Batalkan Invoice Ini?",
      text: "Status invoice akan diubah menjadi Cancelled.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Batalkan!",
      cancelButtonText: "Kembali",
      customClass: { popup: "rounded-3xl" },
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/invoice/cancel/${id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        await Swal.fire({
          title: "Dibatalkan!",
          text: "Invoice berhasil dibatalkan.",
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        fetchInvoices(currentPage, filteredStatus);
      } catch (error) {
        console.error("Error cancelling invoice:", error);
        Swal.fire({
          title: "Gagal Membatalkan",
          text: error.response?.data?.message || "Terjadi kesalahan server.",
          icon: "error",
          customClass: { popup: "rounded-3xl" },
        });
      }
    }
  };

  const pendingInvoices = invoices.filter((i) => i.status === "pending").length;
  const paidInvoices = invoices.filter((i) => i.status === "paid").length;
  const totalAmountSum = invoices.reduce((acc, i) => acc + (i.amount || 0), 0);

  return (
    <div className="space-y-6 pb-12">
      {/* Top Metric Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-4 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Invoice
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : meta.total || invoices.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiFileText className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Belum Lunas (Pending)
            </p>
            <h2 className="text-3xl font-extrabold text-amber-600">
              {loading ? "..." : pendingInvoices}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FiClock className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Invoice Lunas (Paid)
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {loading ? "..." : paidInvoices}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Value Nominal
            </p>
            <h2 className="text-2xl font-extrabold text-emerald-700 truncate max-w-[160px]">
              {loading ? "..." : `Rp ${totalAmountSum.toLocaleString("id-ID")}`}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiDollarSign className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* Controls & Table Container */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nomor invoice / PO..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 text-xs">
          {["all", "pending", "paid", "overdue", "cancelled"].map((st) => (
            <button
              key={st}
              onClick={() => {
                setFilteredStatus(st);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-2xl font-bold transition-all whitespace-nowrap capitalize ${
                filteredStatus === st
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {st}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nomor Invoice</th>
                  <th className="p-3.5">PO Number</th>
                  <th className="p-3.5">Total Nominal</th>
                  <th className="p-3.5">Dibuat Oleh</th>
                  <th className="p-3.5">Jatuh Tempo</th>
                  <th className="p-3.5 text-center">Status</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-12 text-center text-gray-400 font-semibold">
                      Belum ada data invoice
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice, index) => (
                    <tr key={invoice.id || index} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 text-center font-bold text-gray-400">
                        {(meta.current_page - 1) * meta.per_page + index + 1}
                      </td>
                      <td className="p-3.5 font-bold font-mono text-gray-900">{invoice.number}</td>
                      <td className="p-3.5 font-semibold text-gray-700 font-mono">
                        {invoice.po_number || "-"}
                      </td>
                      <td className="p-3.5 font-extrabold text-emerald-700">
                        Rp {(invoice.amount || 0).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 font-semibold text-gray-800">
                        {invoice.created_by || "Admin"}
                      </td>
                      <td className="p-3.5 font-semibold text-gray-600">
                        {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString("id-ID") : "-"}
                      </td>
                      <td className="p-3.5 text-center">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold border capitalize ${
                            statusColors[invoice.status?.toLowerCase()] || "bg-gray-50 text-gray-700 border-gray-200"
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(`/inventory/invoice/detail/${encodeURIComponent(invoice.number)}`)
                            }
                            className="p-2 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-all"
                            title="Lihat Detail Invoice"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>

                          {invoice.status?.toLowerCase() === "pending" && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setIsModalOpen(true);
                                }}
                                className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1"
                              >
                                <FaMoneyBillWave className="w-3.5 h-3.5" />
                                <span>Bayar</span>
                              </button>

                              <button
                                onClick={() => handleCancel(invoice.id)}
                                className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all"
                                title="Batalkan Invoice"
                              >
                                <FaTimesCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Halaman <span className="font-bold text-gray-700">{meta.current_page}</span> dari{" "}
            <span className="font-bold text-gray-700">{meta.last_page}</span> • Total{" "}
            <span className="font-bold text-gray-700">{meta.total}</span> Invoice
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={meta.current_page <= 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                meta.current_page <= 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, meta.last_page))}
              disabled={meta.current_page >= meta.last_page}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                meta.current_page >= meta.last_page
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* MODAL BAYAR INVOICE */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">
                    Upload Bukti Pembayaran
                  </h3>
                  <p className="text-xs text-emerald-700 font-semibold">
                    Invoice #{selectedInvoice?.number}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setEvidenceFile(null);
                  }}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs">
                <div
                  className="border-2 border-dashed border-emerald-300 hover:border-emerald-500 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer bg-emerald-50/30 transition-all group"
                  onClick={() => document.getElementById("fileUploadInput").click()}
                >
                  <input
                    id="fileUploadInput"
                    type="file"
                    onChange={(e) => setEvidenceFile(e.target.files[0])}
                    className="hidden"
                  />
                  <FiUploadCloud className="w-10 h-10 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
                  <p className="text-gray-700 font-semibold text-center">
                    Klik atau drag & drop file bukti pembayaran di sini
                  </p>
                  <p className="text-gray-400 text-[10px] mt-1">Format Gambar / PDF Max 10MB</p>

                  {evidenceFile && (
                    <div className="mt-3 p-2.5 bg-emerald-100 text-emerald-800 rounded-xl font-bold text-xs flex items-center gap-2">
                      <FiCheck className="w-4 h-4 text-emerald-600" />
                      <span className="truncate max-w-[200px]">{evidenceFile.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setEvidenceFile(null);
                  }}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold transition-colors"
                >
                  Batal
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePay}
                  disabled={loadingPay}
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                >
                  <FiCheck className="w-4 h-4" />
                  <span>{loadingPay ? "Memproses..." : "Konfirmasi Lunas"}</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoiceList;
